import React, { useEffect, useState, useCallback } from "react";
import {
  FilmIcon,
  PaperClipIcon,
  TableCellsIcon,
  HomeIcon,
  ClockIcon,
  CalendarIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserBookingsDetails,
  getHostBookingsDetails,
} from "../../../redux/reducers/Properties/PropertiesSlice";
import { AppDispatch, RootState } from "redux/store";
import BackgroundSection from "components/BackgroundSection/BackgroundSection";
import StayCardV4 from "components/StayCard/StayCardV4";
import BookingCard from "components/StayCard/BookingCard";
import HeaderFilterV2 from "containers/PageHome/HeaderFilterV2";
import SectionGridFeaturePlacesSkeleton from "shared/Skelton/SectionGirdSkelton";
import Pagination from "shared/Pagination/Pagination";
import ButtonPrimary from "shared/Button/ButtonPrimary";

interface BookingData {
  id: string;
  [key: string]: string | number | boolean;
}

const MyRequest: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [page, setPage] = useState(1);
  const [bookingData, setBookingData] = useState<BookingData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [metaData, setMetaData] = useState<any>([]);
  const { currentUser } = useSelector((state: RootState) => state.user);
  const [query, setQuery] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const fetchBookings = useCallback(
    async (page: number, searchQuery: string = "", startDate: string = "", endDate: string = "") => {
      setIsLoading(true);
      try {
        if (currentUser?.is_host) {
          const response = await dispatch(
            getHostBookingsDetails({ page, query: searchQuery, startDate, endDate })
          );
          if (response?.payload?.bookings) {
            setBookingData(response.payload.bookings);
            setMetaData(response?.payload?.meta);
          }
        } else {
          const response = await dispatch(
            getUserBookingsDetails({ page, query: searchQuery, startDate, endDate })
          ).unwrap();
          setBookingData(response?.bookings);
          setMetaData(response?.meta);
        }
      } catch (error) {
        console.error("Error fetching bookings:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, currentUser?.is_host]
  );

  useEffect(() => {
    setPage(1);
    fetchBookings(1, searchQuery, startDate, endDate);
  }, [searchQuery, startDate, endDate]);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
    fetchBookings(pageNumber, searchQuery, startDate, endDate);
  };

  const handleSearch = () => {
    setSearchQuery(query);
  };

  const handleClearFilters = () => {
    setQuery("");
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
  };

  const isFilterActive = !!(query || searchQuery || startDate || endDate);

  useEffect(() => {
    if (query === "") {
      fetchBookings(1, "", startDate, endDate);
    }
  }, [query]);

  const rangeFrom = metaData?.total_count ? (page - 1) * 10 + 1 : 0;
  const rangeTo = metaData?.total_count ? Math.min(page * 10, metaData.total_count) : 0;

  if (isLoading && !bookingData.length) {
    return (
      <div className="p-10">
        <SectionGridFeaturePlacesSkeleton />
      </div>
    );
  }

  return (
    <div className="nc-PageHome relative overflow-hidden">
      <div className="container relative space-y-24 mb-24 lg:space-y-28 lg:mb-28">
        <div className="relative py-16">
          <BackgroundSection />
          <div className="nc-SectionGridFeaturePlaces relative">
            <HeaderFilterV2
              heading="My Bookings"
              subHeading="View the complete list of all your booking requests."
            />
            <div className="nc-MyListing">
              <div className="mb-8">
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex flex-col">
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search By Property Name..."
                      className="p-3 w-full md:w-72 border rounded-lg"
                    />
                  </div>
                  <ButtonPrimary
                    onClick={handleSearch}
                    className="px-4 py-2 rounded-lg text-white bg-orange-600 hover:bg-orange-700"
                    disabled={!query.trim()}
                  >
                    Search
                  </ButtonPrimary>
                  <div className="flex flex-col">
                    <label className="text-xs text-neutral-500 mb-1 font-medium">Check-in from</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="p-3 border rounded-lg"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs text-neutral-500 mb-1 font-medium">Check-out to</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="p-3 border rounded-lg"
                    />
                  </div>
                  {isFilterActive && (
                    <button
                      onClick={handleClearFilters}
                      className="px-3 py-2 text-sm text-neutral-600 bg-neutral-100 hover:bg-neutral-200 rounded-lg border border-neutral-200 transition-colors"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              </div>

              {metaData?.total_count > 0 && (
                <div className="mb-3 flex flex-wrap gap-3">
                  <div className="bg-neutral-100 dark:bg-neutral-800 rounded-xl px-4 py-2 flex flex-col items-center min-w-[120px]">
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">Total Bookings</span>
                    <span className="text-xl font-bold text-neutral-800 dark:text-neutral-100">{metaData.total_count}</span>
                  </div>
                  <div className="bg-neutral-100 dark:bg-neutral-800 rounded-xl px-4 py-2 flex flex-col items-center min-w-[200px]">
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">Showing</span>
                    <span className="text-xl font-bold text-neutral-800 dark:text-neutral-100">
                      {rangeFrom}–{rangeTo}{" "}
                      <span className="text-sm font-normal text-neutral-500">of {metaData.total_count}</span>
                    </span>
                  </div>
                </div>
              )}

              {metaData?.total_count > 0 && (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
                  Showing {rangeFrom}–{rangeTo} of {metaData.total_count} bookings
                </p>
              )}

              {bookingData.length > 0 ? (
                <div className="grid grid-cols-1 gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3">
                  {bookingData.map((data) => (
                    <BookingCard key={data.id} booking={data} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <h3 className="text-2xl font-semibold">No bookings found</h3>
                  <p className="text-gray-500 mt-2">
                    There are currently no bookings to display.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        {metaData?.total_count > 0 && (
          <div className="flex mt-8 justify-center items-center space-x-4">
            <Pagination
              currentPage={page}
              totalPages={metaData?.total_pages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MyRequest;
